// Development Profile Dialog: shows top weak criteria for a facilitator
// and matching workshops from the Training Library, with quick "Assign" action.
import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, trainingWorkshopsApi } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  TrendingDown, AlertTriangle, Calendar, Sparkles,
  CheckCircle2, X, GraduationCap, BookOpenCheck
} from 'lucide-react';

export default function DevelopmentProfileDialog({ open, onOpenChange, userId, userName, language = 'el' }) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [assigning, setAssigning] = useState({});

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await usersApi.getDevelopmentProfile(userId);
      setProfile(res.data);
    } catch (e) {
      toast.error(language === 'el' ? 'Σφάλμα φόρτωσης προφίλ' : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId, language]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleAssign = async (workshop) => {
    setAssigning(s => ({ ...s, [workshop.workshop_id]: true }));
    try {
      await trainingWorkshopsApi.assign(workshop.workshop_id, {
        user_id: userId,
        criterion_keys: workshop.matched_criteria || []
      });
      toast.success(language === 'el' ? 'Αναθέθηκε!' : 'Assigned!');
      await load();
    } catch {
      toast.error(language === 'el' ? 'Σφάλμα ανάθεσης' : 'Assignment failed');
    } finally {
      setAssigning(s => ({ ...s, [workshop.workshop_id]: false }));
    }
  };

  const handleUnassign = async (workshop) => {
    setAssigning(s => ({ ...s, [workshop.workshop_id]: true }));
    try {
      await trainingWorkshopsApi.unassign(workshop.workshop_id, userId);
      toast.success(language === 'el' ? 'Αφαιρέθηκε' : 'Removed');
      await load();
    } catch {
      toast.error(language === 'el' ? 'Σφάλμα' : 'Error');
    } finally {
      setAssigning(s => ({ ...s, [workshop.workshop_id]: false }));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch { return iso; }
  };

  const labelFor = (w) => language === 'el' ? w.label_el : w.label_en;
  const categoryFor = (w) => language === 'el' ? w.category_el : w.category_en;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="development-profile-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E6007E] to-purple-500 flex items-center justify-center shrink-0">
              <Sparkles className="text-white" size={18} />
            </div>
            <div>
              <DialogTitle className="text-xl">
                {language === 'el' ? 'Προφίλ Ανάπτυξης' : 'Development Profile'}
                {userName ? <span className="text-gray-400 font-normal"> · {userName}</span> : null}
              </DialogTitle>
              <DialogDescription>
                {language === 'el'
                  ? 'Ανάλυση επαναλαμβανόμενων αδυναμιών και προτεινόμενα workshops'
                  : 'Recurring weaknesses and recommended workshops'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center text-gray-500 text-sm">
            {language === 'el' ? 'Φόρτωση...' : 'Loading...'}
          </div>
        )}

        {!loading && profile && (
          <div className="space-y-6 mt-2">
            {/* Summary strip */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">
                  {language === 'el' ? 'Αξιολογήσεις' : 'Evaluations'}
                </div>
                <div className="text-2xl font-bold text-[#2A1514]">{profile.total_evaluations}</div>
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                <div className="text-[10px] uppercase tracking-wide text-amber-700">
                  {language === 'el' ? 'Αδυναμίες' : 'Weak areas'}
                </div>
                <div className="text-2xl font-bold text-amber-700">{profile.top_weaknesses.length}</div>
              </div>
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                <div className="text-[10px] uppercase tracking-wide text-emerald-700">
                  {language === 'el' ? 'Ανατεθειμένα' : 'Assigned'}
                </div>
                <div className="text-2xl font-bold text-emerald-700">{profile.assignments_count}</div>
              </div>
            </div>

            {/* Top weaknesses */}
            <section>
              <h3 className="font-semibold text-sm text-[#2A1514] flex items-center gap-2 mb-2">
                <TrendingDown size={16} className="text-amber-600" />
                {language === 'el' ? 'Top 3 περιοχές προς ανάπτυξη' : 'Top 3 areas needing development'}
              </h3>
              {profile.top_weaknesses.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center">
                  <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm text-gray-500">
                    {language === 'el'
                      ? 'Καμία επαναλαμβανόμενη αδυναμία — εξαιρετική απόδοση!'
                      : 'No recurring weak areas — solid performance!'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {profile.top_weaknesses.map((w, i) => {
                    const level = w.low_count >= 3 ? 'red' : w.low_count === 2 ? 'orange' : 'yellow';
                    const flagClass = level === 'red'
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : level === 'orange'
                      ? 'bg-orange-50 border-orange-200 text-orange-700'
                      : 'bg-yellow-50 border-yellow-200 text-yellow-700';
                    const dotClass = level === 'red' ? 'bg-red-500' : level === 'orange' ? 'bg-orange-500' : 'bg-yellow-500';
                    return (
                      <div key={w.criterion_key}
                           className={`rounded-xl border p-3 flex items-center gap-3 ${flagClass}`}
                           data-testid={`weakness-${i}`}>
                        <div className={`w-2 h-10 rounded-full ${dotClass}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{labelFor(w)}</div>
                          <div className="text-[11px] opacity-70">{categoryFor(w)}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="flex items-center gap-1 text-sm font-semibold">
                            <AlertTriangle size={12} />
                            ×{w.low_count}
                          </div>
                          <div className="text-[10px] opacity-70">
                            {language === 'el' ? 'Μ.Ο.' : 'Avg'}: {w.average.toFixed(1)}/5
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Recommended workshops */}
            <section>
              <h3 className="font-semibold text-sm text-[#2A1514] flex items-center gap-2 mb-2">
                <GraduationCap size={16} className="text-[#E6007E]" />
                {language === 'el' ? 'Προτεινόμενα Workshops' : 'Recommended Workshops'}
              </h3>
              {profile.top_weaknesses.length === 0 ? null : profile.recommended_workshops.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center">
                  <BookOpenCheck size={28} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">
                    {language === 'el'
                      ? 'Δεν βρέθηκαν workshops που να καλύπτουν αυτές τις περιοχές. Ταγκάρετε ένα workshop με τα αντίστοιχα κριτήρια.'
                      : 'No workshops match these areas yet. Tag a workshop with the matching criteria.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {profile.recommended_workshops.map(w => (
                    <div key={w.workshop_id}
                         className="rounded-xl border border-gray-200 p-3 flex items-center gap-3 hover:border-[#E6007E]/40 transition-colors"
                         data-testid={`recommended-workshop-${w.workshop_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#2A1514] truncate">{w.name}</div>
                        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-500">
                          <span className="flex items-center gap-1"><Calendar size={11} />{fmtDate(w.date)}</span>
                          {w.trainer && <span>· {w.trainer}</span>}
                        </div>
                        {w.matched_criteria && w.matched_criteria.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {w.matched_criteria.map(k => {
                              const found = profile.top_weaknesses.find(x => x.criterion_key === k);
                              return (
                                <Badge key={k} variant="outline"
                                       className="text-[9px] py-0 px-1.5 border-[#E6007E]/30 text-[#E6007E] bg-[#E6007E]/5">
                                  {found ? labelFor(found) : k}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {w.already_assigned ? (
                        <Button size="sm" variant="outline"
                                disabled={assigning[w.workshop_id]}
                                onClick={() => handleUnassign(w)}
                                className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                data-testid={`unassign-btn-${w.workshop_id}`}>
                          <CheckCircle2 size={13} className="mr-1" />
                          {language === 'el' ? 'Ανατεθειμένο' : 'Assigned'}
                          <X size={11} className="ml-1.5 opacity-60" />
                        </Button>
                      ) : (
                        <Button size="sm"
                                disabled={assigning[w.workshop_id]}
                                onClick={() => handleAssign(w)}
                                className="text-xs bg-[#E6007E] hover:bg-[#b8005f]"
                                data-testid={`assign-btn-${w.workshop_id}`}>
                          {assigning[w.workshop_id]
                            ? '...'
                            : (language === 'el' ? 'Ανάθεση' : 'Assign')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
